/**
 * Operator portal PR 3 (migration 0206) — which haulers' data an operator sees.
 * GET/POST /v1/platform/operator-hauler-access (MANAGE_OPERATOR_PORTAL_ACCESS).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';

export interface OperatorHaulerAccessRow {
  readonly haulerTenantId: string;
  readonly haulerName: string;
  readonly haulerAbbreviation: string | null;
  readonly haulerIsActive: boolean;
  /** Paelode's decision: ACTIVE, REVOKED, or null when never decided. */
  readonly accessStatus: 'ACTIVE' | 'REVOKED' | null;
  readonly accessSource: 'MANUAL' | 'SUGGESTION' | 'BACKFILL' | null;
  readonly confirmedAt: string | null;
  readonly revokedAt: string | null;
  readonly note: string | null;
  /** The hauler's half: it maps at least one customer to this operator. */
  readonly mapsCustomer: boolean;
  readonly activeCustomerRows: number;
  /** Both halves hold: the operator sees this hauler's data. */
  readonly visible: boolean;
}

export interface OperatorHaulerAccessList {
  /** The operator's merge-chain head, which the rows are keyed on. */
  readonly platformOperatorId: string;
  readonly haulers: readonly OperatorHaulerAccessRow[];
}

export interface HaulerAccessChange {
  platformOperatorId: string;
  haulerTenantId: string;
  note?: string;
}

const listKey = (operatorId: string): readonly string[] => [
  'platform',
  'operator-hauler-access',
  operatorId,
];

export function useOperatorHaulerAccess(
  operatorId: string,
): ReturnType<typeof useApiQuery<OperatorHaulerAccessList>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorHaulerAccessList>(
    opts,
    `/v1/platform/operator-hauler-access?platformOperatorId=${operatorId}`,
    { queryKey: listKey(operatorId), staleTime: 15_000 },
  );
}

function useAccessChange(
  operatorId: string,
  path: string,
): ReturnType<
  typeof useApiMutation<HaulerAccessChange, { accessStatus: string | null; changed: boolean }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<HaulerAccessChange, { accessStatus: string | null; changed: boolean }>(
    opts,
    path,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: listKey(operatorId) });
      },
    },
  );
}

export function useGrantHaulerAccess(operatorId: string): ReturnType<typeof useAccessChange> {
  return useAccessChange(operatorId, '/v1/platform/operator-hauler-access');
}

export function useRevokeHaulerAccess(operatorId: string): ReturnType<typeof useAccessChange> {
  return useAccessChange(operatorId, '/v1/platform/operator-hauler-access/revoke');
}

// ── Operator portal PR 4 (migration 0211): match-up suggestions ───────────────

export interface OperatorHaulerSuggestion {
  readonly id: string;
  readonly platformOperatorId: string;
  readonly haulerTenantId: string;
  readonly haulerName: string;
  readonly haulerAbbreviation: string | null;
  /** 0.90 to 1 (only those are ever suggested). */
  readonly score: number;
  readonly evidence: {
    readonly E1?: { readonly customerRows: number };
    readonly E2?: { readonly loads: number };
    readonly E3?: { readonly loads: number };
    readonly E4?: { readonly similarity: number; readonly customerName: string };
    readonly E5?: boolean;
    readonly windowDays?: number;
  };
  readonly status: 'OPEN' | 'CONFIRMED' | 'DISMISSED';
}

const suggestionsKey = (operatorId: string): readonly string[] => [
  'platform',
  'operator-hauler-suggestions',
  operatorId,
];

export function useOperatorHaulerSuggestions(
  operatorId: string,
): ReturnType<typeof useApiQuery<{ suggestions: readonly OperatorHaulerSuggestion[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ suggestions: readonly OperatorHaulerSuggestion[] }>(
    opts,
    `/v1/platform/operator-hauler-suggestions?platformOperatorId=${operatorId}`,
    { queryKey: suggestionsKey(operatorId), staleTime: 15_000 },
  );
}

/** Confirm (gives access) or dismiss one suggestion; refreshes both lists. */
export function useDecideSuggestion(
  operatorId: string,
  suggestionId: string,
  decision: 'confirm' | 'dismiss',
): ReturnType<typeof useApiMutation<Record<string, never>, { status: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { status: string }>(
    opts,
    `/v1/platform/operator-hauler-suggestions/${suggestionId}/${decision}`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: suggestionsKey(operatorId) });
        void qc.invalidateQueries({ queryKey: listKey(operatorId) });
      },
    },
  );
}

export function useRecomputeSuggestions(
  operatorId: string,
): ReturnType<typeof useApiMutation<{ platformOperatorId: string }, Record<string, number>>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ platformOperatorId: string }, Record<string, number>>(
    opts,
    '/v1/platform/operator-hauler-suggestions/recompute',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: suggestionsKey(operatorId) });
      },
    },
  );
}

/** The evidence in words, for the admin. */
export function describeEvidence(e: OperatorHaulerSuggestion['evidence']): string[] {
  const days = e.windowDays ?? 180;
  const out: string[] = [];
  if (e.E1 !== undefined) out.push('maps a customer to this operator');
  if (e.E2 !== undefined) {
    out.push(
      `${String(e.E2.loads)} load${e.E2.loads === 1 ? '' : 's'} at this operator's wells (last ${String(days)} days)`,
    );
  }
  if (e.E3 !== undefined) {
    out.push(`${String(e.E3.loads)} load${e.E3.loads === 1 ? '' : 's'} for that customer`);
  }
  if (e.E4 !== undefined) {
    out.push(
      `a private customer "${e.E4.customerName}" with a similar name (${String(Math.round(e.E4.similarity * 100))}%)`,
    );
  }
  if (e.E5 === true) out.push('with the same abbreviation');
  return out;
}
